import type { CSSProperties, ReactNode } from "react";
import { Legend as RechartsLegend, Tooltip as RechartsTooltip } from "recharts";

/**
 * Minimal shadcn-style chart utilities adapted for this project.
 *
 * The API mirrors the shadcn/ui chart container so feature code
 * can be migrated later with minimal changes.
 */

export type ChartConfig = Record<
  string,
  {
    label: string;
    /** Optional explicit color for the series (CSS color value). */
    color?: string;
  }
>;

const DEFAULT_PALETTE: string[] = [
  "hsl(222, 84%, 56%)", // chart-1
  "hsl(210, 40%, 80%)", // chart-2
  "hsl(160, 84%, 39%)", // chart-3
  "hsl(48, 96%, 53%)",  // chart-4
  "hsl(340, 82%, 52%)", // chart-5
];

export interface ChartContainerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /**
   * Series configuration. Keys should match data keys used in charts.
   * Colors fall back to a shadcn-like palette when not provided.
   */
  config?: ChartConfig;
  children: ReactNode;
}

export const ChartContainer = (
  props: ChartContainerProps,
): React.JSX.Element => {
  const { config, style, children, ...rest } = props;

  const cssVars: CSSProperties = { ...style };

  if (config) {
    let index = 0;
    for (const [, value] of Object.entries(config)) {
      const color = value.color ?? DEFAULT_PALETTE[index % DEFAULT_PALETTE.length];
      const cssVarName = `--chart-${index + 1}` as keyof CSSProperties;
      // Store as CSS custom property for use inside Recharts components.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (cssVars as any)[cssVarName] = color;
      index += 1;
    }
  }

  return (
    <div
      role="figure"
      data-chart-container
      style={cssVars}
      {...rest}
    >
      {children}
    </div>
  );
};


export interface ChartTooltipContentProps {
  active?: boolean;
  label?: unknown;
  payload?: {
    color?: string;
    name?: unknown;
    value?: unknown;
    dataKey?: unknown;
  }[];
  config?: ChartConfig;
}

export const ChartTooltipContent = (
  props: ChartTooltipContentProps,
): React.JSX.Element | null => {
  const { active, payload, label } = props;

  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const safePayload = payload.filter((entry) => entry && entry.value != null);

  if (safePayload.length === 0) {
    return null;
  }

  return (
    <div className="inline-flex flex-col gap-1 py-[0.45rem] px-[0.7rem] rounded-lg border border-slate-400/40 bg-slate-900/92 text-gray-200 text-xs leading-[1.2] shadow-[0_10px_15px_-3px_rgba(15,23,42,0.7),0_4px_6px_-4px_rgba(15,23,42,0.6)]">
      {label !== undefined && label !== null && (
        <div className="font-medium text-[#cbd5f5] mb-[0.15rem]">{String(label)}</div>
      )}
      <div className="flex flex-col gap-[0.15rem]">
        {safePayload.map((entry, index) => {
          const key = `${String(entry.dataKey ?? entry.name ?? index)}-${index}`;
          const color =
            typeof entry.color === "string" && entry.color.length > 0
              ? entry.color
              : undefined;
          const rawValue = entry.value;
          const numericValue =
            typeof rawValue === "number"
              ? rawValue
              : Number(rawValue ?? 0);
          const name =
            typeof entry.name === "string" && entry.name.length > 0
              ? entry.name
              : "";

          return (
            <div key={key} className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-[0.35rem]">
                {color ? (
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color }}
                  />
                ) : null}
                <span className="text-gray-200">{name}</span>
              </div>
              <span className="tabular-nums font-semibold text-gray-50">
                {Number.isFinite(numericValue)
                  ? numericValue.toLocaleString()
                  : String(rawValue ?? "")}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export type ChartTooltipProps = Omit<
  React.ComponentProps<typeof RechartsTooltip>,
  "content"
> & {
  config?: ChartConfig;
};

export const ChartTooltip = (
  props: ChartTooltipProps,
): React.JSX.Element => {
  const { config, ...rest } = props;

  return (
    <RechartsTooltip
      {...rest}
      content={<ChartTooltipContent config={config} />}
      wrapperStyle={{ outline: "none" }}
      contentStyle={{
        background: "transparent",
        border: "none",
        boxShadow: "none",
        padding: 0,
      }}
    />
  );
};

export interface ChartLegendContentProps {
  payload?: {
    color?: string;
    value?: unknown;
    dataKey?: unknown;
  }[];
  config?: ChartConfig;
}

export const ChartLegendContent = (
  props: ChartLegendContentProps,
): React.JSX.Element | null => {
  const { payload } = props;

  if (!payload || payload.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2 gap-x-3 text-xs">
      {payload.map((entry, index) => {
        if (!entry) {
          return null;
        }

        const color =
          typeof entry.color === "string" && entry.color.length > 0
            ? entry.color
            : undefined;
        const label =
          typeof entry.value === "string" && entry.value.length > 0
            ? entry.value
            : String(entry.dataKey ?? "");
        const key = `${label}-${index}`;

        return (
          <div key={key} className="flex items-center gap-[0.35rem]">
            {color ? (
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: color }}
              />
            ) : null}
            <span className="text-gray-600">{label}</span>
          </div>
        );
      })}
    </div>
  );
};

export type ChartLegendProps = Omit<
  React.ComponentProps<typeof RechartsLegend>,
  "content"
> & {
  config?: ChartConfig;
};

export const ChartLegend = (
  props: ChartLegendProps,
): React.JSX.Element => {
  const { config, ...rest } = props;

  return (
    <RechartsLegend
      {...rest}
      content={<ChartLegendContent config={config} />}
    />
  );
};

